import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from 'src/services/redux/app/store';
import { logsRead } from 'src/services/redux/slices/logs/logs-slice';
import { notificationsRead, notificationsUnreadCount } from 'src/services/redux/slices/notifications/notifications-slice';
import { _clearLogs, _clearNotifications } from 'src/services/redux/slices/unread/unreads-slice';
import { KTSVG, toAbsoluteUrl } from '../../../helpers';
import { HeaderNotificationsMenu, HeaderUserMenu, MessagesSummaryMenu, QuickLinks, WorkflowSummaryMenu } from '../../../partials';
import { getLayout, ILayout, LayoutSetup, useLayout } from '../../core';
import { useMarketPlaceWorkPlaceInquiryList, usePendingExtensionRequestsCount } from '../../service';
import Restricted from 'src/app/permissions/restricted';
import { isFeedbackHandler } from 'src/app/components/support-feedback/constants';
import { usePendingSupportRequestsCount } from 'src/app/components/support-feedback/service';

const toolbarButtonMarginClass = 'ms-1 ms-lg-3',
  toolbarButtonHeightClass = 'w-30px h-30px w-md-40px h-md-40px',
  toolbarUserAvatarHeightClass = 'symbol-30px symbol-md-30px',
  toolbarButtonIconSizeClass = 'svg-icon-1';

// Talepler menüsünü görebilecek kullanıcı mailleri
const TALEPLER_ALLOWED_EMAILS = ['namik@aynetteknoloji.com.tr', 'cihancan.mustaoglu@aynetteknoloji.com.tr'];

const Topbar: FC = () => {
  const dispatch = useAppDispatch();
  const [notification, setNotification] = useState<boolean>(false);
  const [userMenu, setUserMenu] = useState<boolean>(false);
  const { unreads } = useAppSelector((state) => state?.unreads);
  const { unreadCount } = useAppSelector((state) => state?.notifications);
  const { user } = useAppSelector((state) => state.auth);
  const canSeeTalepler = !!user?.email && TALEPLER_ALLOWED_EMAILS.includes(user.email.trim().toLowerCase());
  const { data: pendingTaleplerCount = 0 } = usePendingExtensionRequestsCount(canSeeTalepler);
  // Geri bildirim rozeti — sadece geri bildirimi yöneten kullanıcıda
  const isFbHandler = isFeedbackHandler(user?.email);
  const { data: pendingFeedbackCount = 0 } = usePendingSupportRequestsCount(isFbHandler);
  // Notlar ikonu sarı + hareketli vurgu — sadece Faruk Güngör için
  const isFaruk = user?.email?.trim().toLowerCase() === 'faruk.gungor@aynetteknoloji.com.tr';
  const { config, setLayout } = useLayout();
  const [configLayout, setConfigLayout] = useState<ILayout>(getLayout());

  // Component mount olduğunda okunmamış bildirim sayısını getir
  useEffect(() => {
    if (user?.id) {
      dispatch(notificationsUnreadCount());
    }
  }, [dispatch, user?.id]);

  const updateData = (fieldsToUpdate: Partial<ILayout>) => {
    const updatedData = { ...configLayout, ...fieldsToUpdate };
    setConfigLayout(updatedData);
    try {
      LayoutSetup.setConfig(updatedData);
    } catch (error) {
      setConfigLayout(getLayout());
    }
    setLayout(updatedData);
  };
  return (
    <div className="d-flex align-items-stretch flex-shrink-0">
      {/* Search */}
      {/* <div className={clsx('d-flex align-items-stretch', toolbarButtonMarginClass)}>
        <Search />
      </div> */}

      {/* Talepler - sadece yetkili maillere görünür */}
      {canSeeTalepler && (
        <div className="d-flex align-items-center ms-1 ms-lg-3">
          <Link to="/admin/talepler" className="btn btn-sm btn-light-primary d-flex align-items-center fw-bold px-3 position-relative" title="Süre Uzatma Talepleri">
            <KTSVG path="/media/icons/duotone/Communication/Clipboard-list.svg" className="svg-icon-2 me-1" />
            Talepler
            {Number(pendingTaleplerCount) > 0 && (
              <span
                className="position-absolute translate-middle badge badge-danger rounded-pill border border-2 border-white fw-bold d-flex align-items-center justify-content-center"
                style={{ top: 0, left: '100%', minWidth: 18, height: 18, padding: '0 5px', fontSize: 10, lineHeight: 1 }}
              >
                {Number(pendingTaleplerCount) > 99 ? '99+' : pendingTaleplerCount}
              </span>
            )}
          </Link>
        </div>
      )}

      {/* Transfers - Transferler */}
      <div className="d-flex align-items-center ms-1 ms-lg-3">
        <Link to="/transfers" className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px" title="Transferler">
          <KTSVG path="/media/icons/duotone/Files/Folder-thunder.svg" className="svg-icon-1" />
        </Link>
      </div>

      {/* Notes - Notlarım */}
      <Restricted to="ROLE_NOTES">
        <div className="d-flex align-items-center ms-1 ms-lg-3">
          <Link to="/todos" className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px" title="Notlarım">
            <span
              className="d-inline-flex"
              style={isFaruk ? { animation: 'notes-pulse 1.5s ease-in-out infinite', display: 'inline-flex' } : undefined}
            >
              <KTSVG path="/media/icons/duotone/General/Clipboard.svg" className={clsx('svg-icon-1', isFaruk && 'svg-icon-warning')} />
            </span>
            {isFaruk && (
              <style>{`
                @keyframes notes-pulse {
                  0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(255, 199, 0, 0)); }
                  50% { transform: scale(1.25); filter: drop-shadow(0 0 6px rgba(255, 199, 0, 0.9)); }
                }
              `}</style>
            )}
          </Link>
        </div>
      </Restricted>

      {/* Mesajlar - önizleme dropdown'ı (son 5 sohbet) */}
      <div className={clsx('d-flex align-items-center ', toolbarButtonMarginClass)}>
        <div
          className={clsx(`btn btn-icon btn-active-light-primary position-relative message-joyride`, toolbarButtonHeightClass)}
          data-kt-menu-trigger="click"
          data-kt-menu-attach="parent"
          data-kt-menu-placement="bottom-end"
          data-kt-menu-flip="bottom"
          title="Mesajlar"
        >
          <KTSVG path="/media/icons/duotone/Communication/Chat2.svg" className={toolbarButtonIconSizeClass} />
          {Number(unreads?.messages_unread_count) !== 0 && (
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">{unreads?.messages_unread_count}</span>
          )}
        </div>
        <MessagesSummaryMenu />
      </div>

      {/* <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <div
          className={clsx(`btn btn-icon btn-active-light-primary position-relative `, toolbarButtonHeightClass)}
          id="kt_activities_toggle"
          onClick={() => {
            if (unreads?.logs_unread_count !== 0) {
              dispatch(logsRead());
              dispatch(_clearLogs());
            }
          }}
        >
          {unreads?.logs_unread_count !== 0 && (
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">{unreads?.logs_unread_count}</span>
          )}
          <KTSVG path="/media/icons/duotone/Media/Equalizer.svg" className={toolbarButtonIconSizeClass} />
        </div>
      </div> */}

      {/* Quick links - Workflow Summary */}
      <Restricted to="ROLE_WORKFLOWS">
        <div className="d-flex align-items-center ms-1 ms-lg-3">
          <div
            className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px"
            data-kt-menu-trigger="click"
            data-kt-menu-attach="parent"
            data-kt-menu-placement="bottom-end"
            data-kt-menu-flip="bottom"
          >
            <span className="svg-icon svg-icon-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
                <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                  <rect x="0" y="0" width="24" height="24" />
                  <rect fill="#000000" x="4" y="4" width="7" height="7" rx="1.5" />
                  <path
                    d="M5.5,13 L9.5,13 C10.3284271,13 11,13.6715729 11,14.5 L11,18.5 C11,19.3284271 10.3284271,20 9.5,20 L5.5,20 C4.67157288,20 4,19.3284271 4,18.5 L4,14.5 C4,13.6715729 4.67157288,13 5.5,13 Z M14.5,4 L18.5,4 C19.3284271,4 20,4.67157288 20,5.5 L20,9.5 C20,10.3284271 19.3284271,11 18.5,11 L14.5,11 C13.6715729,11 13,10.3284271 13,9.5 L13,5.5 C13,4.67157288 13.6715729,4 14.5,4 Z M14.5,13 L18.5,13 C19.3284271,13 20,13.6715729 20,14.5 L20,18.5 C20,19.3284271 19.3284271,20 18.5,20 L14.5,20 C13.6715729,20 13,19.3284271 13,18.5 L13,14.5 C13,13.6715729 13.6715729,13 14.5,13 Z"
                    fill="#000000"
                    opacity="0.3"
                  />
                </g>
              </svg>
            </span>
          </div>
          <WorkflowSummaryMenu />
        </div>
      </Restricted>

      {/* NOTIFICATIONS - yoruma alındı, bildirimler Bana Özel sayfasında */}
      {/* <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <div
          className={clsx(`btn btn-icon btn-active-light-primary position-relative notification-joyride`, toolbarButtonHeightClass)}
          data-kt-menu-trigger="click"
          data-kt-menu-attach="parent"
          data-kt-menu-placement="bottom-end"
          data-kt-menu-flip="bottom"
          onClick={() => {
            setNotification(!notification);
            setUserMenu(false);
          }}
        >
          <KTSVG path="/media/icons/duotone/General/Notifications1.svg" className={toolbarButtonIconSizeClass} />
          {unreadCount > 0 && <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </div>
        {<HeaderNotificationsMenu show={notification} onClose={() => setNotification(false)} />}
      </div> */}

      <div
        className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}
        onClick={() => {
          updateData({
            aside: {
              ...config.aside,
              theme: config.aside.theme === 'dark' ? 'light' : 'dark',
            },
          });
        }}
      >
        {/* begin::Menu- wrapper */}
        {/* <div
          className={clsx('btn btn-icon btn-active-light-primary position-relative dark-mode', toolbarButtonHeightClass)}
          data-kt-menu-trigger="click"
          data-kt-menu-attach="parent"
          data-kt-menu-placement="bottom-end"
          data-kt-menu-flip="bottom"
        >
          {config.aside.theme === 'light' ? (
            <KTSVG path={`/media/icons/duotone/Weather/Sun.svg`} className={toolbarButtonIconSizeClass} />
          ) : (
            <KTSVG path={`/media/icons/duotone/Weather/Moon.svg`} className={toolbarButtonIconSizeClass} />
          )}
        </div> */}
        {/* end::Menu wrapper */}
      </div>

      {/* Bana Özel */}
      <div className="d-flex align-items-center ms-1">
        <Link to="/my-hub" className={clsx('btn btn-icon btn-active-light-primary position-relative', toolbarButtonHeightClass)} title="Bana Özel">
          <span
            className={toolbarButtonIconSizeClass}
            style={{
              animation: 'myhub-pulse 2s ease-in-out infinite',
              display: 'inline-flex',
            }}
          >
            <KTSVG path="/media/icons/duotone/General/Thunder-move.svg" className="svg-icon-1 svg-icon-primary" />
          </span>
          <style>{`
            @keyframes myhub-pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.2); }
            }
          `}</style>
        </Link>
      </div>

      {/* begin::User */}
      <div className={clsx('d-flex align-items-center  ', toolbarButtonMarginClass)} id="kt_header_user_menu_toggle">
        {/* begin::Toggle */}
        <div
          className={clsx('cursor-pointer symbol position-relative account-settings', toolbarUserAvatarHeightClass)}
          data-kt-menu-trigger="click"
          data-kt-menu-attach="parent"
          data-kt-menu-placement="bottom-end"
          data-kt-menu-flip="bottom"
        >
          <div
            className="p-2 symbol-label fs-6 fw-bold bg-primary text-white d-flex align-items-center justify-content-center"
            style={{ width: '100%', height: '100%', borderRadius: '6px' }}
          >
            {user?.name?.charAt(0)?.toUpperCase()}
            {user?.surname?.charAt(0)?.toUpperCase()}
          </div>
          {isFbHandler && Number(pendingFeedbackCount) > 0 && (
            <span
              className="position-absolute translate-middle badge badge-danger rounded-pill border border-2 border-white fw-bold d-flex align-items-center justify-content-center"
              style={{ top: 0, left: '100%', minWidth: 18, height: 18, padding: '0 5px', fontSize: 10, lineHeight: 1, zIndex: 1 }}
              title="Yeni geri bildirim"
            >
              {Number(pendingFeedbackCount) > 99 ? '99+' : pendingFeedbackCount}
            </span>
          )}
        </div>
        <HeaderUserMenu show={userMenu} />

        {/* end::Toggle */}
      </div>
      {/* end::User */}

      {/* begin::Aside Toggler */}
      {config.header.left === 'menu' && (
        <div className="d-flex align-items-center d-lg-none ms-2 me-n3" title="Show header menu">
          <div className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px" id="kt_header_menu_mobile_toggle">
            <KTSVG path="/media/icons/duotone/Text/Toggle-Right.svg" className="svg-icon-1" />
          </div>
        </div>
      )}
    </div>
  );
};

export { Topbar };
